// components/ProcessCanvas.js
// Interactive process flow diagram builder with thermodynamic stream calculations.
// Supports: drag-to-place nodes, click-to-connect streams, manual solve, parameter editing.
/* eslint-disable no-unused-vars */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { NODE_TYPES, PALETTE, COLOR_MAP, portPosition } from './canvas/canvasNodes';
import { COMP_LIST, N_COMP, mixtureMW, emptyStream } from './canvas/canvasThermo';

// ─── ID generator ─────────────────────────────────────────────────────────────
let _id = Date.now();
const uid = () => {
  const id = _id++;
  return `${id.toString(36)}`;
};
const sid = (a, b) => `${a}__${b}`;   // edge id: sourceNodeId__sourcePortKey

// ─── Flowsheet solver (recycle-aware successive substitution) ─────────────────
//
// Strategy:
//   1. Build the directed graph of nodes and edges.
//   2. Identify recycle streams (edges that create cycles) via DFS back-edge detection.
//   3. Tear the recycle streams: seed them with an initial guess (feed composition).
//   4. Solve all nodes in topological order of the torn graph.
//   5. Check convergence of torn stream compositions.
//   6. Repeat up to MAX_RECYCLE_ITER times.
//
// This handles HX → Sep → HX loops, absorber recycle, stripper lean-oil recycle, etc.

const MAX_RECYCLE_ITER = 80;   // default — can be overridden per call
const RECYCLE_TOL = 0.001;     // 0.1% change in any tear stream property

function solveFlowsheet(nodes, edges, opts = {}) {
  const maxIter  = opts.maxIter  ?? MAX_RECYCLE_ITER;
  const tol      = opts.tol      ?? RECYCLE_TOL;
  const damp     = opts.damp     ?? 0.5;
  const startFrom = opts.startFrom ?? null;

  if (nodes.length === 0) return { resultStore: {}, streamStore: {}, streamByEdge: {}, tearEdgeIds: new Set(), converged: false, iterations: 0 };

  // ── Deduplicate edges at solver level ─────────────────────────────────────
  // Each outlet port can only feed one downstream connection.
  // Each inlet port can only receive from one upstream connection.
  // Keep the last edge added (highest index in array) when duplicates exist.
  // This permanently prevents stale edges from corrupting the solve topology.
  const seenOut = new Map(); // `fromNode:fromPort` → edge
  const seenIn  = new Map(); // `toNode:toPort` → edge
  const nodeById = {};
  nodes.forEach(n => { nodeById[n.id] = n; });

  // Keep FIRST-added edge when duplicates exist (JSON edges come first, stale user-drawn
  // edges come later). Process in original order, skip if port already claimed.
  const edges_deduped = [];
  edges.forEach(e => {
    if (!nodeById[e.fromNode] || !nodeById[e.toNode]) return;
    const outKey = `${e.fromNode}:${e.fromPort}`;
    const toNodeType = nodeById[e.toNode]?.type;
    const allowMultiIn = (toNodeType === 'MIXER');
    const inKey = allowMultiIn ? null : `${e.toNode}:${e.toPort}`;
    if (seenOut.has(outKey)) return; // duplicate outlet — skip (keep first)
    if (inKey && seenIn.has(inKey)) return; // duplicate inlet — skip (keep first)
    seenOut.set(outKey, e);
    if (inKey) seenIn.set(inKey, e);
    edges_deduped.push(e);
  });
  const edgesForSolve = edges_deduped;

  // ── Build adjacency ───────────────────────────────────────────────────────
  // outEdges[nodeId] = [edge, ...]; inEdges[nodeId] = [edge, ...]
  const outEdges = {};
  const inEdges  = {};
  nodes.forEach(n => { outEdges[n.id] = []; inEdges[n.id] = []; });
  edgesForSolve.forEach(e => {
    outEdges[e.fromNode].push(e);
    inEdges[e.toNode].push(e);
  });

  // ── Detect back-edges (recycle tears) via DFS ─────────────────────────────
  const visited  = new Set();
  const onStack  = new Set();
  const tearEdges = new Set(); // edge ids that are recycle tears

  function dfs(nid) {
    visited.add(nid);
    onStack.add(nid);
    for (const e of outEdges[nid]) {
      if (!nodeById[e.toNode]) continue;
      if (!visited.has(e.toNode)) {
        dfs(e.toNode);
      } else if (onStack.has(e.toNode)) {
        tearEdges.add(e.id); // back-edge = recycle
      }
    }
    onStack.delete(nid);
  }
  nodes.forEach(n => { if (!visited.has(n.id)) dfs(n.id); });

  // ── Relocate tears on PUMP outputs to pump inlets ─────────────────────────
  // If a PUMP with setFlow_kmolh > 0 has its output edge torn, move the tear
  // to the edge entering the pump instead. This lets the pump's fixed flow
  // propagate forward through the recycle (still → ROLO → pump → absorber)
  // rather than having the pump output seeded externally.
  tearEdges.forEach(eid => {
    const e = edgesForSolve.find(x => x.id === eid);
    if (!e) return;
    const srcNode = nodeById[e.fromNode];
    if (srcNode?.type === 'PUMP' && srcNode.params.setFlow_kmolh > 0) {
      // Find the edge entering this pump
      const pumpInEdge = edgesForSolve.find(x => x.toNode === srcNode.id);
      if (pumpInEdge && !tearEdges.has(pumpInEdge.id)) {
        tearEdges.delete(eid);
        tearEdges.add(pumpInEdge.id);
      }
    }
  });

  // ── Topological order of the torn graph ──────────────────────────────────
  // Ignore tear edges for ordering purposes
  const inDegTorn = {};
  nodes.forEach(n => { inDegTorn[n.id] = 0; });
  edgesForSolve.forEach(e => {
    if (!tearEdges.has(e.id) && nodeById[e.fromNode] && nodeById[e.toNode]) {
      inDegTorn[e.toNode]++;
    }
  });
  const topoQueue = nodes.filter(n => inDegTorn[n.id] === 0).map(n => n.id);
  const topoOrder = [];
  const tmpSet = new Set(topoQueue);
  while (topoQueue.length > 0) {
    const nid = topoQueue.shift();
    topoOrder.push(nid);
    for (const e of outEdges[nid]) {
      if (tearEdges.has(e.id) || !nodeById[e.toNode]) continue;
      inDegTorn[e.toNode]--;
      if (inDegTorn[e.toNode] === 0 && !tmpSet.has(e.toNode)) {
        topoQueue.push(e.toNode);
        tmpSet.add(e.toNode);
      }
    }
  }
  // Any nodes not in topoOrder (pure cycles with no feed) — append them
  nodes.forEach(n => { if (!tmpSet.has(n.id)) topoOrder.push(n.id); });

  // ── Stream store: edge id → stream ────────────────────────────────────────
  const streamByEdge = {};

  // Helper: create a fully independent stream (all arrays copied, no shared references)
  function independentSeed(base, overrides = {}) {
    return {
      ...base,
      ...overrides,
      z: new Float64Array(overrides.z || base.z),  // always a fresh copy
      valid: true,
    };
  }

  // Seed tear streams smarter: use the first FEED node found as base
  const allFeeds = nodes.filter(n => n.type === 'FEED').map(fn => {
    try {
      const def = NODE_TYPES[fn.type];
      const res = def.solve(fn.params, {});
      return Object.values(res.streams)[0];
    } catch (_) { return null; }
  }).filter(Boolean);

  const defaultSeed = allFeeds[0] || emptyStream('seed');

  // Build lean oil seed from pump node with setFlow_kmolh > 0
  // The pump IS the surge tank — it defines the lean oil circulation rate and composition
  const pumpNode = nodes.find(n => n.type === 'PUMP' && n.params.setFlow_kmolh > 0);
  const leanOilZ_base = new Float64Array(N_COMP); // indices: C7=4, C8=5, C9=6
  leanOilZ_base[4] = 0.06; // C7
  leanOilZ_base[5] = 0.50; // C8
  leanOilZ_base[6] = 0.44; // C9

  const leanOilSeedBase = pumpNode ? {
    id: 'leanSeed',
    T_K: 298.15,  // 25°C — lean oil after aircooler
    P_bar: pumpNode.params.P2_bar || 58,
    F_kmols: pumpNode.params.setFlow_kmolh,
    z: leanOilZ_base,
    phase: 'L',
    valid: true,
  } : allFeeds.find(s => s.z[0] < 0.5) || defaultSeed;

  tearEdges.forEach(eid => {
    const e = edgesForSolve.find(x => x.id === eid);
    if (!e) return;

    const toNode = nodes.find(n => n.id === e.toNode);
    const srcNode = nodeById[e.fromNode];

    const isLeanOilPort = e.toPort === 'solventIn' ||
      (e.toPort === 'in' && toNode?.type === 'STRIPPER') ||
      (e.toPort === 'in' && toNode?.type === 'AIRCOOLER') ||
      (e.toPort === 'hotIn' && toNode?.type === 'HX') ||
      (e.toPort === 'in' && toNode?.type === 'PUMP');

    // Temperature appropriate to where this tear stream enters
    let seedT_K = leanOilSeedBase.T_K;
    if (e.toPort === 'hotIn' && toNode?.type === 'HX') {
      const stillNode = nodes.find(n => n.type === 'STRIPPER');
      seedT_K = stillNode?.params.rebT_C ? (stillNode.params.rebT_C + 273.15) : 537.15;
    } else if (e.toPort === 'in' && toNode?.type === 'AIRCOOLER') {
      seedT_K = 420;  // ~147°C between still and aircooler
    }

    // Always create an independent seed with its own z copy — no shared references
    const baseSeed = isLeanOilPort ? leanOilSeedBase : defaultSeed;
    streamByEdge[eid] = independentSeed(baseSeed, { id: eid, T_K: seedT_K });

    // Set pressure from source node
    const P_src = srcNode?.params.P2_bar ?? srcNode?.params.P_bar;
    if (P_src) streamByEdge[eid].P_bar = P_src;
  });

  // If continuing from a previous solve, overlay existing computed tear values
  if (startFrom) {
    tearEdges.forEach(eid => {
      if (startFrom[eid]?.valid) streamByEdge[eid] = startFrom[eid];
    });
  }

  // ── Successive substitution loop ──────────────────────────────────────────
  const resultStore = {};
  let converged = false;
  let iterations = 0;
  let maxErr = 1.0;

  for (let outerIter = 0; outerIter < maxIter; outerIter++) {
    iterations = outerIter + 1;
    // ── Capture snapshot of tear streams for convergence check ──────────────
    const snapshot = {};
    tearEdges.forEach(eid => {
      if (streamByEdge[eid]) {
        snapshot[eid] = {
          F: streamByEdge[eid].F_kmols,
          T: streamByEdge[eid].T_K,
          P: streamByEdge[eid].P_bar,
          z: new Float64Array(streamByEdge[eid].z)
        };
      }
    });

    // Solve in topological order
    for (const nid of topoOrder) {
      const node = nodeById[nid];
      if (!node) continue;
      const def = NODE_TYPES[node.type];
      if (!def) continue;

      // Re-build inlet map from current streamByEdge
      const nodeInlets = {};
      edgesForSolve.filter(e => e.toNode === nid).forEach(e => {
        if (streamByEdge[e.id]) nodeInlets[e.toPort] = streamByEdge[e.id];
      });

      let solveResult;
      try {
        solveResult = def.solve(node.params, nodeInlets);
      } catch (err) {
        solveResult = { streams: {}, results: { Error: err.message.slice(0, 60) } };
      }
      resultStore[nid] = solveResult.results || {};

      // Propagate outlet streams onto outgoing edges
      // IMPORTANT: always copy z to prevent shared Float64Array reference corruption
      const outStreams = solveResult.streams || {};
      for (const e of outEdges[nid]) {
        const s = outStreams[e.fromPort];
        if (s && s.valid !== false) {
          const isTear = tearEdges.has(e.id);
          if (isTear && streamByEdge[e.id] && outerIter > 0) {
            // Damped update on tear streams: blend old and new to aid convergence
            const old = streamByEdge[e.id];
            const newZ = new Float64Array(s.z.length);
            for (let i = 0; i < newZ.length; i++) {
              newZ[i] = (1 - damp) * old.z[i] + damp * s.z[i];
            }
            streamByEdge[e.id] = {
              ...s, id: e.id,
              z: newZ,
              F_kmols: (1 - damp) * old.F_kmols + damp * s.F_kmols,
              T_K:     (1 - damp) * old.T_K     + damp * s.T_K,
            };
          } else {
            // Non-tear edges or first iteration: store directly with fresh z copy
            streamByEdge[e.id] = { ...s, id: e.id, z: new Float64Array(s.z) };
          }
        }
      }
    }

    // Check convergence on tear streams
    let maxErr = 0;
    tearEdges.forEach(eid => {
      const prev = snapshot[eid];
      const next = streamByEdge[eid];
      if (!prev || !next) { maxErr = 1.0; return; }
      
      // Molar flow error
      const errF = Math.abs(next.F_kmols - prev.F) / Math.max(prev.F, 1.0);
      maxErr = Math.max(maxErr, errF);

      // Temperature error
      const errT = Math.abs(next.T_K - prev.T) / 300;
      maxErr = Math.max(maxErr, errT);

      // Pressure error
      const errP = Math.abs(next.P_bar - prev.P) / Math.max(prev.P, 1.0);
      maxErr = Math.max(maxErr, errP);

      // Composition error
      for (let i = 0; i < prev.z.length; i++) {
        maxErr = Math.max(maxErr, Math.abs(next.z[i] - prev.z[i]));
      }
    });

    if (tearEdges.size === 0 || maxErr < tol) {
      converged = true;
      break;
    }
  }

  // Tag recycle nodes with convergence status if there are tears
  if (tearEdges.size > 0) {
    tearEdges.forEach(eid => {
      const e = edgesForSolve.find(x => x.id === eid);
      if (!e) return;
      const nid = e.toNode;
      if (resultStore[nid]) {
        resultStore[nid]['Recycle'] = converged ? '✓ converged' : '⚠ not converged';
      }
    });
  }

  // Any nodes that never got solved
  nodes.forEach(n => {
    if (!resultStore[n.id]) resultStore[n.id] = { Status: 'Unsolved (check connections)' };
  });

  // Build streamStore: nodeId → { portKey: stream }
  const streamStore = {};
  nodes.forEach(n => { streamStore[n.id] = {}; });
  edgesForSolve.forEach(e => {
    if (streamByEdge[e.id]) {
      streamStore[e.toNode] = streamStore[e.toNode] || {};
      streamStore[e.toNode][e.toPort] = streamByEdge[e.id];
    }
  });

  // Track which edge IDs were skipped by dedup (stale edges)
  const usedEdgeIds = new Set(edgesForSolve.map(e => e.id));
  const skippedEdgeIds = new Set(edges.filter(e => !usedEdgeIds.has(e.id)).map(e => e.id));

  return { resultStore, streamStore, streamByEdge, tearEdgeIds: tearEdges, converged, iterations, maxErr, skippedEdgeIds };
}

// ─── Stream composition table helper ─────────────────────────────────────────
function StreamTable({ stream }) {
  if (!stream || !stream.valid) return <div style={{ color: 'var(--color-text-tertiary)', fontSize: 12 }}>No stream data</div>;
  const MW = mixtureMW(stream.z);
  return (
    <div style={{ fontSize: 11, marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 4, color: 'var(--color-text-secondary)' }}>
        <span>T: {(stream.T_K - 273.15).toFixed(1)}°C</span>
        <span>P: {stream.P_bar.toFixed(2)} bar</span>
        <span>F: {stream.F_kmols.toFixed(1)} kmol/h</span>
        <span>MW: {MW.toFixed(2)}</span>
        <span>Phase: {stream.phase}</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border-tertiary)' }}>
            <th style={{ textAlign: 'left', padding: '2px 4px', fontWeight: 500 }}>Component</th>
            <th style={{ textAlign: 'right', padding: '2px 4px', fontWeight: 500 }}>mol%</th>
            <th style={{ textAlign: 'right', padding: '2px 4px', fontWeight: 500 }}>kmol/h</th>
          </tr>
        </thead>
        <tbody>
          {COMP_LIST.map((c, i) => stream.z[i] > 0.0001 && (
            <tr key={c.name} style={{ borderBottom: '1px solid var(--color-border-tertiary)' }}>
              <td style={{ padding: '2px 4px' }}>{c.name}</td>
              <td style={{ textAlign: 'right', padding: '2px 4px' }}>{(stream.z[i] * 100).toFixed(2)}</td>
              <td style={{ textAlign: 'right', padding: '2px 4px' }}>{(stream.z[i] * stream.F_kmols).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Parameter editor ─────────────────────────────────────────────────────────
function ParamEditor({ node, onChange, results }) {
  const def = NODE_TYPES[node.type];
  if (!def) return null;
  const params = node.params;

  // Params that are boolean toggles (stored as 0/1)
  const BOOLEAN_PARAMS = new Set(['rigorous', 'partial']);

  // Separate composition params from regular params
  const compNames = new Set(COMP_LIST.map(c => c.name));
  const regularKeys = Object.keys(params).filter(k => !compNames.has(k));
  const compKeys = COMP_LIST.map(c => c.name).filter(c => c in params);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {regularKeys.map(k => {
        const isBoolean = BOOLEAN_PARAMS.has(k);
        const isInt = ['N_stages', 'feedTray', 'lkIdx', 'hkIdx'].includes(k);
        const isText = k === 'name';
        return (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ flex: 1, fontSize: 12, color: 'var(--color-text-secondary)' }}>
              {k}
              {k === 'lkIdx' && <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}> (0=CH4 1=C2 2=C3 3=C4 4=C7 5=C8 6=C9 7=N2)</span>}
              {k === 'hkIdx' && <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}> (heavy key — same index)</span>}
            </label>
            {isBoolean ? (
              <input
                type="checkbox"
                checked={!!params[k]}
                onChange={e => onChange(k, e.target.checked ? 1 : 0)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
            ) : isText ? (
              <input
                type="text"
                value={params[k] || ''}
                onChange={e => onChange(k, e.target.value)}
                style={{
                  width: 80, padding: '3px 6px', fontSize: 12,
                  background: 'var(--color-background-secondary)',
                  border: '1px solid var(--color-border-tertiary)',
                  borderRadius: 4, color: 'var(--color-text-primary)',
                }}
              />
            ) : (
              <input
                type="number"
                value={params[k]}
                step={isInt ? 1 : 'any'}
                onChange={e => onChange(k, +e.target.value)}
                style={{
                  width: 80, padding: '3px 6px', fontSize: 12,
                  background: 'var(--color-background-secondary)',
                  border: '1px solid var(--color-border-tertiary)',
                  borderRadius: 4, color: 'var(--color-text-primary)',
                }}
              />
            )}
          </div>
        );
      })}

      {compKeys.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4, fontWeight: 500 }}>Composition (mol%)</div>
          {compKeys.map(k => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ flex: 1, fontSize: 12, color: 'var(--color-text-secondary)' }}>{k}</label>
              <input
                type="number"
                value={params[k]}
                step="0.01"
                min="0"
                onChange={e => onChange(k, +e.target.value)}
                style={{
                  width: 70, padding: '3px 6px', fontSize: 12,
                  background: 'var(--color-background-secondary)',
                  border: '1px solid var(--color-border-tertiary)',
                  borderRadius: 4, color: 'var(--color-text-primary)',
                }}
              />
            </div>
          ))}
        </>
      )}

      {/* Tray temperature profile for rigorous distillation */}
      {node.type === 'DISTCOL' && params.rigorous && results?.['Cond T (°C)'] && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>
            Tray T profile
          </div>
          <div style={{ fontSize: 10, color: 'var(--color-text-secondary)' }}>
            Top: {results['Cond T (°C)']}°C → Bottom: {results['Reb T (°C)']}°C
          </div>
        </div>
      )}
    </div>
  );
}

// ─── P&ID symbol library ──────────────────────────────────────────────────────
// Each function receives (w, h, colors, selected, strokeW) and returns SVG elements.
// Coordinates are relative to the node's top-left (0,0).

function PidVessel({ w, h, colors, strokeW, packingLines = false }) {
  // Vertical pressure vessel: cylindrical shell + elliptical heads
  const hd = h * 0.12; // head height
  const cx = w / 2, bw = w * 0.7, bx = (w - bw) / 2;
  const numLines = packingLines ? 4 : 0;
  return (
    <>
      {/* Shell body */}
      <rect x={bx} y={hd} width={bw} height={h - 2 * hd}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Top elliptical head */}
      <ellipse cx={cx} cy={hd} rx={bw / 2} ry={hd}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Bottom elliptical head */}
      <ellipse cx={cx} cy={h - hd} rx={bw / 2} ry={hd}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Packing hatching for absorber/stripper */}
      {packingLines && Array.from({ length: numLines }, (_, i) => {
        const y = hd + (h - 2 * hd) * (i + 1) / (numLines + 1);
        return (
          <line key={i} x1={bx + 2} y1={y} x2={bx + bw - 2} y2={y}
            stroke={colors.border} strokeWidth={0.8} strokeDasharray="3,2" opacity={0.6} />
        );
      })}
    </>
  );
}

function PidHX({ w, h, colors, strokeW, variant }) {
  // Circle with resistor coil inside — ISA heat exchanger symbol
  const cx = w / 2, cy = h / 2, r = Math.min(w, h) / 2 - 3;
  // Resistor coil: horizontal zigzag across the circle diameter
  const coilY = cy, coilX1 = cx - r * 0.6, coilX2 = cx + r * 0.6;
  const pts = 5, seg = (coilX2 - coilX1) / pts;
  const zigzag = Array.from({ length: pts + 1 }, (_, i) => {
    const x = coilX1 + i * seg;
    const y = coilY + (i % 2 === 0 ? -7 : 7);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  const isChiller = variant === 'chiller';
  return (
    <>
      <circle cx={cx} cy={cy} r={r}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Dividing line — tube sheet */}
      <line x1={cx - r} y1={cy} x2={cx + r} y2={cy}
        stroke={colors.border} strokeWidth={0.8} strokeDasharray="4,2" opacity={0.5} />
      {/* Coil */}
      <path d={zigzag} fill="none" stroke={colors.border} strokeWidth={1.5}
        strokeLinecap="round" strokeLinejoin="round" />
      {/* Snowflake for chiller */}
      {isChiller && (
        <text x={cx} y={cy - r * 0.35} textAnchor="middle"
          fontSize={11} fill={colors.border} opacity={0.9}>❄</text>
      )}
    </>
  );
}

function PidCompressor({ w, h, colors, strokeW }) {
  // Centrifugal compressor: filled triangle pointing right (flow left→right)
  // Inlet at left apex, outlet at right wide face
  const pts = `0,${h / 2} ${w},0 ${w},${h}`;
  return (
    <polygon points={pts}
      fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
  );
}

function PidPump({ w, h, colors, strokeW }) {
  // Pump: circle on left 2/3, rectangle "volute" tab on right 1/3
  const r = h / 2 - 2;
  const cx = r + 4, cy = h / 2;
  const tabX = cx + r - 2, tabW = w - tabX - 2, tabH = h * 0.45;
  const tabY = cy - tabH / 2;
  // Rotation arrow inside circle
  const arrowR = r * 0.45;
  const arc = `M ${cx + arrowR} ${cy} A ${arrowR} ${arrowR} 0 1 1 ${cx} ${cy - arrowR}`;
  return (
    <>
      <circle cx={cx} cy={cy} r={r}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Discharge nozzle / volute rectangle */}
      <rect x={tabX} y={tabY} width={tabW} height={tabH} rx={2}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Rotation arc arrow */}
      <path d={arc} fill="none" stroke={colors.border} strokeWidth={1.2}
        strokeLinecap="round" markerEnd="url(#pumpArrow)" />
    </>
  );
}

function PidFiredHeater({ w, h, colors, strokeW }) {
  // Rectangle box with zigzag burner at bottom and three flame triangles
  const bh = h * 0.6; // box height
  const by = h - bh;
  const cx = w / 2;
  // Flames
  const fw = 10, fh = 15;
  const flames = [cx - 18, cx, cx + 18];
  // Zigzag burner bar
  const nz = 6, zw = w * 0.7, zx = (w - zw) / 2;
  const seg = zw / nz;
  const zpath = Array.from({ length: nz + 1 }, (_, i) => {
    const x = zx + i * seg;
    const y = by + bh - 6 + (i % 2 === 0 ? 0 : 4);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <>
      {/* Furnace box */}
      <rect x={0} y={by} width={w} height={bh} rx={3}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Stack / flue nozzle on top */}
      <rect x={cx - 5} y={0} width={10} height={by + 6} rx={2}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Flames */}
      {flames.map((fx, i) => (
        <polygon key={i}
          points={`${fx},${by + bh - 8} ${fx - fw / 2},${by + 8} ${fx + fw / 2},${by + 8}`}
          fill={colors.border} opacity={0.55} />
      ))}
      {/* Burner zigzag */}
      <path d={zpath} fill="none" stroke={colors.border}
        strokeWidth={1.5} strokeLinecap="round" />
    </>
  );
}

function PidReboiler({ w, h, colors, strokeW }) {
  // Kettle reboiler: horizontal vessel with vapour nozzle on top right
  const hd = w * 0.1;
  const cy = h / 2, bh = h * 0.7, by = (h - bh) / 2;
  const nozzleX = w * 0.75, nozzleH = by;
  return (
    <>
      {/* Horizontal shell */}
      <rect x={hd} y={by} width={w - 2 * hd} height={bh} rx={2}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Left head */}
      <ellipse cx={hd} cy={cy} rx={hd} ry={bh / 2}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Right head */}
      <ellipse cx={w - hd} cy={cy} rx={hd} ry={bh / 2}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Vapour nozzle top right */}
      <rect x={nozzleX - 5} y={0} width={10} height={nozzleH + 2} rx={2}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Liquid level line */}
      <line x1={hd + 2} y1={cy + bh * 0.15} x2={w - hd - 2} y2={cy + bh * 0.15}
        stroke={colors.border} strokeWidth={0.7} strokeDasharray="4,2" opacity={0.5} />
    </>
  );
}

function PidFeed({ w, h, colors, strokeW }) {
  // Right-pointing filled flag arrow
  const aw = w * 0.4, ah = h, tip = w;
  return (
    <polygon
      points={`0,0 ${w - aw},0 ${tip},${h / 2} ${w - aw},${h} 0,${h}`}
      fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
  );
}

function PidSink({ w, h, colors, strokeW }) {
  // Left-pointing empty flag with X
  const aw = w * 0.4;
  const cx = w * 0.4, cy = h / 2, xr = h * 0.22;
  return (
    <>
      <polygon
        points={`${w},0 ${aw},0 0,${h / 2} ${aw},${h} ${w},${h}`}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* X mark */}
      <line x1={cx - xr} y1={cy - xr} x2={cx + xr} y2={cy + xr}
        stroke={colors.border} strokeWidth={1.5} />
      <line x1={cx + xr} y1={cy - xr} x2={cx - xr} y2={cy + xr}
        stroke={colors.border} strokeWidth={1.5} />
    </>
  );
}

function PidMixer({ w, h, colors, strokeW }) {
  const cx = w / 2, cy = h / 2, r = Math.min(w, h) / 2 - 2;
  const d = r * 0.6;
  return (
    <>
      <circle cx={cx} cy={cy} r={r}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      <line x1={cx - d} y1={cy - d} x2={cx + d} y2={cy + d}
        stroke={colors.border} strokeWidth={1.5} />
      <line x1={cx + d} y1={cy - d} x2={cx - d} y2={cy + d}
        stroke={colors.border} strokeWidth={1.5} />
    </>
  );
}

function PidSplitter({ w, h, colors, strokeW }) {
  const cx = w / 2, cy = h / 2, r = Math.min(w, h) / 2 - 2;
  return (
    <>
      <circle cx={cx} cy={cy} r={r}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      <line x1={cx} y1={cy} x2={cx} y2={cy + r * 0.6} stroke={colors.border} strokeWidth={1.5} />
      <line x1={cx} y1={cy} x2={cx - r * 0.5} y2={cy - r * 0.6} stroke={colors.border} strokeWidth={1.5} />
      <line x1={cx} y1={cy} x2={cx + r * 0.5} y2={cy - r * 0.6} stroke={colors.border} strokeWidth={1.5} />
    </>
  );
}

function PidValve({ w, h, colors, strokeW }) {
  // ISA globe valve / control valve: two triangles meeting at tip (bowtie), with stem
  const cx = w / 2, cy = h / 2;
  const hw = w * 0.42, hh = h * 0.35;
  return (
    <>
      {/* Left triangle */}
      <polygon points={`${cx - hw},${cy - hh} ${cx - hw},${cy + hh} ${cx},${cy}`}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Right triangle */}
      <polygon points={`${cx + hw},${cy - hh} ${cx + hw},${cy + hh} ${cx},${cy}`}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Actuator stem */}
      <line x1={cx} y1={cy - hh} x2={cx} y2={cy - hh - 8}
        stroke={colors.border} strokeWidth={strokeW} />
      <rect x={cx - 6} y={cy - hh - 13} width={12} height={6} rx={1}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW * 0.8} />
    </>
  );
}

function PidDistCol({ w, h, colors, strokeW }) {
  // Tall distillation column vessel with tray lines and feed nozzle
  const hd = h * 0.09;
  const cx = w / 2, bw = w * 0.72, bx = (w - bw) / 2;
  const nTrays = 5;
  return (
    <>
      {/* Shell */}
      <rect x={bx} y={hd} width={bw} height={h - 2 * hd}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Top elliptical head */}
      <ellipse cx={cx} cy={hd} rx={bw / 2} ry={hd}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Bottom elliptical head */}
      <ellipse cx={cx} cy={h - hd} rx={bw / 2} ry={hd}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Tray lines */}
      {Array.from({ length: nTrays }, (_, i) => {
        const y = hd + (h - 2 * hd) * (i + 1) / (nTrays + 1);
        return <line key={i} x1={bx + 1} y1={y} x2={bx + bw - 1} y2={y}
          stroke={colors.border} strokeWidth={0.9} opacity={0.55} />;
      })}
      {/* Feed nozzle stub on left at mid-height */}
      <line x1={0} y1={h / 2} x2={bx} y2={h / 2}
        stroke={colors.border} strokeWidth={strokeW} />
    </>
  );
}

function PidPartialCond({ w, h, colors, strokeW }) {
  // Partial condenser: HX circle + small accumulator drum below
  const cx = w / 2, r = w * 0.33;
  const cy = r + 4;
  // Accumulator drum (small horizontal vessel)
  const drumY = cy + r + 8, drumH = h - drumY - 4;
  const drumW = w * 0.65, drumX = (w - drumW) / 2;
  const hdrum = drumH * 0.35;
  const coilY = cy;
  const coilX1 = cx - r * 0.55, coilX2 = cx + r * 0.55;
  const pts = 4, seg = (coilX2 - coilX1) / pts;
  const zigzag = Array.from({ length: pts + 1 }, (_, i) =>
    `${i === 0 ? 'M' : 'L'}${(coilX1 + i * seg).toFixed(1)},${(coilY + (i % 2 === 0 ? -5 : 5)).toFixed(1)}`
  ).join(' ');
  return (
    <>
      {/* Condenser circle */}
      <circle cx={cx} cy={cy} r={r}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      <path d={zigzag} fill="none" stroke={colors.border} strokeWidth={1.4}
        strokeLinecap="round" strokeLinejoin="round" />
      {/* Connecting line to drum */}
      <line x1={cx} y1={cy + r} x2={cx} y2={drumY}
        stroke={colors.border} strokeWidth={strokeW} />
      {/* Accumulator drum */}
      <rect x={drumX + hdrum} y={drumY} width={drumW - 2 * hdrum} height={drumH}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      <ellipse cx={drumX + hdrum} cy={drumY + drumH / 2} rx={hdrum} ry={drumH / 2}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      <ellipse cx={drumX + drumW - hdrum} cy={drumY + drumH / 2} rx={hdrum} ry={drumH / 2}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
    </>
  );
}

function PidAirCooler({ w, h, colors, strokeW }) {
  // Aerial cooler: rectangular bundle with fin lines across top, fan circle below
  const bx = 4, by = 4, bw = w - 8, bh = h * 0.55;
  const nFins = 6;
  const fanCx = w / 2, fanCy = h * 0.82, fanR = h * 0.14;
  return (
    <>
      {/* Bundle rectangle */}
      <rect x={bx} y={by} width={bw} height={bh}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Fin lines */}
      {Array.from({ length: nFins }, (_, i) => {
        const x = bx + (i + 1) * bw / (nFins + 1);
        return <line key={i} x1={x} y1={by} x2={x} y2={by + bh}
          stroke={colors.border} strokeWidth={0.8} opacity={0.6} />;
      })}
      {/* Arrow indicating air flow downward */}
      <line x1={w/2} y1={by + bh + 2} x2={w/2} y2={fanCy - fanR - 1}
        stroke={colors.border} strokeWidth={1} markerEnd="none" />
      {/* Fan circle */}
      <circle cx={fanCx} cy={fanCy} r={fanR}
        fill={colors.bg} stroke={colors.border} strokeWidth={strokeW} />
      {/* Fan blades — simple X inside circle */}
      <line x1={fanCx - fanR * 0.65} y1={fanCy - fanR * 0.65}
            x2={fanCx + fanR * 0.65} y2={fanCy + fanR * 0.65}
        stroke={colors.border} strokeWidth={1} />
      <line x1={fanCx + fanR * 0.65} y1={fanCy - fanR * 0.65}
            x2={fanCx - fanR * 0.65} y2={fanCy + fanR * 0.65}
        stroke={colors.border} strokeWidth={1} />
    </>
  );
}

function renderPIDSymbol(type, w, h, colors, selected) {
  const strokeW = selected ? 2.5 : 1.5;
  switch (type) {
    case 'FEED':         return <PidFeed         w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'SINK':         return <PidSink         w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'SEPARATOR':    return <PidVessel       w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'ABSORBER':     return <PidVessel       w={w} h={h} colors={colors} strokeW={strokeW} packingLines />;
    case 'STRIPPER':     return <PidVessel       w={w} h={h} colors={colors} strokeW={strokeW} packingLines />;
    case 'DISTCOL':      return <PidDistCol      w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'PARTIAL_COND': return <PidPartialCond  w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'MIXER':        return <PidMixer        w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'SPLITTER':     return <PidSplitter     w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'VALVE':        return <PidValve        w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'HEATER':       return <PidFiredHeater  w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'AIRCOOLER':    return <PidAirCooler    w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'HX':           return <PidHX           w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'CHILLER':      return <PidHX           w={w} h={h} colors={colors} strokeW={strokeW} variant="chiller" />;
    case 'REBOILER':     return <PidReboiler     w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'COMPRESSOR':   return <PidCompressor   w={w} h={h} colors={colors} strokeW={strokeW} />;
    case 'PUMP':         return <PidPump         w={w} h={h} colors={colors} strokeW={strokeW} />;
    default: return <rect x={0} y={0} width={w} height={h} rx={6} fill={colors.bg} stroke={colors.border} strokeWidth={1.5} />;
  }
}

// ─── Node SVG renderer ────────────────────────────────────────────────────────
function CanvasNode({ node, selected, results, onSelect, onDragStart, connectingFrom, onPortClick, streamStore }) {
  const def = NODE_TYPES[node.type];
  if (!def) return null;
  const colors = COLOR_MAP[def.color] || COLOR_MAP.gray;
  const { w, h } = def;
  const isConnecting = !!connectingFrom;

  // First result value for inline display
  const firstResult = results && Object.entries(results)[0];

  return (
    <g
      transform={`translate(${node.x},${node.y})`}
      style={{ cursor: 'grab', userSelect: 'none' }}
      onMouseDown={e => { e.stopPropagation(); onDragStart(e, node.id); }}
      onClick={e => { e.stopPropagation(); onSelect(node.id); }}
    >
      {/* Selection glow */}
      {selected && (
        <rect x={-4} y={-4} width={w + 8} height={h + 8} rx={10}
          fill="none" stroke="#2196f3" strokeWidth={2} strokeDasharray="4,2" opacity={0.7} />
      )}

      {/* P&ID symbol — all drawing done here, no generic rect */}
      {renderPIDSymbol(node.type, w, h, colors, selected)}

      {/* Node user label (name) below symbol */}
      <text x={w / 2} y={h + 15} textAnchor="middle"
        fontSize={11} fontWeight={600} fill={colors.text}>
        {node.params.name || def.label}
      </text>

      {/* First result value shown below user label */}
      {firstResult && (
        <text x={w / 2} y={h + 28} textAnchor="middle"
          fontSize={9} fill={colors.text} opacity={0.85}>
          {firstResult[1]}
        </text>
      )}

      {/* Ports — rendered on top of symbol */}
      {def.ports.map(port => {
        const pos = portPosition({ ...node, x: 0, y: 0 }, port.key);
        const isTarget = isConnecting && port.kind === 'in';
        const isSource = !isConnecting && port.kind === 'out';
        const stream = streamStore?.[node.id]?.[port.key];
        const hasStream = stream && stream.valid && stream.F_kmols > 0;

        const portColor = hasStream
          ? (stream.phase === 'V' ? '#2196f3' : stream.phase === 'L' ? '#4caf50' : '#ff9800')
          : (isTarget ? '#2196f3' : isSource ? colors.border : '#aaa');

        return (
          <g key={port.key}
            onClick={e => { e.stopPropagation(); onPortClick(node.id, port.key, port.kind); }}
            style={{ cursor: 'crosshair' }}>
            {/* Large transparent hit area — easy to click */}
            <circle cx={pos.x} cy={pos.y} r={16} fill="transparent" />
            {/* Hover ring — shown via CSS :hover on the group */}
            <circle cx={pos.x} cy={pos.y} r={10}
              fill="transparent"
              stroke={portColor}
              strokeWidth={1}
              opacity={0.3}
              className="port-hover-ring" />
            {/* Visible port dot */}
            <circle cx={pos.x} cy={pos.y} r={5}
              fill={hasStream || isTarget ? portColor : 'var(--color-background-primary)'}
              stroke={portColor}
              strokeWidth={isTarget ? 2.5 : 1.5} />
            {/* Port label */}
            {port.label && (
              <text
                x={port.side === 'left'   ? pos.x - 9  : port.side === 'right' ? pos.x + 9  : pos.x}
                y={port.side === 'top'    ? pos.y - 9  : port.side === 'bottom' ? pos.y + 9  : pos.y}
                textAnchor={port.side === 'left' ? 'end' : port.side === 'right' ? 'start' : 'middle'}
                dominantBaseline={port.side === 'top' ? 'auto' : port.side === 'bottom' ? 'hanging' : 'central'}
                fontSize={8} fill={colors.text} opacity={0.7}
                style={{ pointerEvents: 'none' }}>
                {port.label}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}

// ─── Edge (stream connection) ─────────────────────────────────────────────────
function CanvasEdge({ edge, nodes, streamByEdge, selected, isTear, isSkipped, onClick }) {
  const fromNode = nodes.find(n => n.id === edge.fromNode);
  const toNode   = nodes.find(n => n.id === edge.toNode);
  if (!fromNode || !toNode) return null;

  const p1 = portPosition(fromNode, edge.fromPort);
  const p2 = portPosition(toNode,   edge.toPort);

  const dx = Math.abs(p2.x - p1.x) * 0.5 + 40;
  const path = `M${p1.x},${p1.y} C${p1.x + dx},${p1.y} ${p2.x - dx},${p2.y} ${p2.x},${p2.y}`;

  const stream = streamByEdge?.[edge.id];
  const streamColor = !stream || !stream.valid || stream.F_kmols === 0 ? '#888'
    : stream.phase === 'V' ? '#2196f3'
    : stream.phase === 'L' ? '#4caf50'
    : '#ff9800';

  // Stale edges (removed by dedup): shown in red with dashes — solver ignores them
  if (isSkipped) {
    const mx = (p1.x + p2.x) / 2;
    const my = (p1.y + p2.y) / 2;
    return (
      <g onClick={e => { e.stopPropagation(); onClick(edge.id); }} style={{ cursor: 'pointer' }}>
        <path d={path} fill="none" stroke="transparent" strokeWidth={14} />
        <path d={path} fill="none" stroke="#ef4444" strokeWidth={1.5}
          strokeDasharray="4,3" opacity={0.7} />
        {/* Warning badge at midpoint */}
        <circle cx={mx} cy={my} r={8} fill="#ef4444" opacity={0.9} />
        <text x={mx} y={my} textAnchor="middle" dominantBaseline="central"
          fontSize={10} fill="white" style={{ pointerEvents: 'none' }}>✕</text>
        <title>Stale connection — ignored by solver (duplicate port). Click to select and delete.</title>
      </g>
    );
  }

  const strokeColor = selected ? '#2196f3' : isTear ? '#ff9800' : streamColor;
  const strokeW = selected ? 3 : 2;

  return (
    <g onClick={e => { e.stopPropagation(); onClick(edge.id); }} style={{ cursor: 'pointer' }}>
      <path d={path} fill="none" stroke="transparent" strokeWidth={14} />
      <path d={path} fill="none"
        stroke={strokeColor}
        strokeWidth={strokeW}
        strokeDasharray={
          isTear ? '8,4' :
          stream?.phase === 'L' ? '6,3' : undefined
        }
        markerEnd="url(#streamArrow)" />
      {stream?.valid && stream.F_kmols > 0 && (
        <text
          x={(p1.x + p2.x) / 2}
          y={(p1.y + p2.y) / 2 - 8}
          textAnchor="middle" fontSize={9}
          fill={strokeColor}
          style={{ pointerEvents: 'none' }}>
          {stream.F_kmols.toFixed(0)} kmol/h
        </text>
      )}
    </g>
  );
}

// ─── Main ProcessCanvas component ─────────────────────────────────────────────

export default function ProcessCanvas() {
  const [nodes, setNodes]       = useState([]);
  const [edges, setEdges]       = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [results, setResults]   = useState({});
  const [streamStore, setStreamStore] = useState({});
  const [streamByEdge, setStreamByEdge] = useState({});
  const [tearEdgeIds, setTearEdgeIds] = useState(new Set());
  const [skippedEdgeIds, setSkippedEdgeIds] = useState(new Set()); // stale edges removed by dedup
  const [connectingFrom, setConnectingFrom] = useState(null); // {nodeId, portKey}
  const [tempLine, setTempLine] = useState(null); // {x1,y1,x2,y2} while dragging edge
  const [pan, setPan]           = useState({ x: 0, y: 0 });
  const [zoom, setZoom]         = useState(1);
  const [dragging, setDragging] = useState(null); // {nodeId, ox, oy}
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState(null);
  const [solved, setSolved]     = useState(false);
  const [activeTab, setActiveTab] = useState('properties'); // 'properties' | 'streams'
  const [solveIter, setSolveIter] = useState(0);       // iterations used in last solve
  const [solveConverged, setSolveConverged] = useState(false);
  const [solveMaxErr, setSolveMaxErr] = useState(null);
  const [maxIter, setMaxIter]   = useState(80);
  const [solveTol, setSolveTol] = useState(0.001);
  const [solveDamp, setSolveDamp] = useState(0.5);
  const [streamByEdgePersist, setStreamByEdgePersist] = useState({}); // persists between steps
  const svgRef = useRef(null);

  // ── Undo/Redo State ────────────────────────────────────────────────────────
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);

  const saveHistory = useCallback((currentNodes, currentEdges) => {
    setHistory(prev => {
      const nextHistory = prev.slice(0, historyIdx + 1);
      return [...nextHistory, {
        nodes: JSON.parse(JSON.stringify(currentNodes)),
        edges: JSON.parse(JSON.stringify(currentEdges))
      }].slice(-50);
    });
    setHistoryIdx(prev => Math.min(prev + 1, 49));
  }, [historyIdx]);

  const undo = useCallback(() => {
    if (historyIdx > 0) {
      const prevState = history[historyIdx - 1];
      setNodes(prevState.nodes);
      setEdges(prevState.edges);
      setHistoryIdx(historyIdx - 1);
      setSolved(false);
    }
  }, [history, historyIdx]);

  const redo = useCallback(() => {
    if (historyIdx < history.length - 1) {
      const nextState = history[historyIdx + 1];
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      setHistoryIdx(historyIdx + 1);
      setSolved(false);
    }
  }, [history, historyIdx]);

  // Initial history point
  useEffect(() => {
    if (history.length === 0) {
      setHistory([{ nodes: [], edges: [] }]);
      setHistoryIdx(0);
    }
  }, [history.length]);

  const selectedNode = nodes.find(n => n.id === selectedId);
  const selectedEdge = edges.find(e => e.id === selectedEdgeId);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const svgPoint = useCallback((e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top  - pan.y) / zoom,
    };
  }, [pan, zoom]);

  const [spaceDown, setSpaceDown] = useState(false);

  // ── Drag node ────────────────────────────────────────────────────────────────
  const handleDragStart = useCallback((e, nodeId) => {
    if (connectingFrom || spaceDown) return;
    const pt = svgPoint(e);
    const node = nodes.find(n => n.id === nodeId);
    setDragging({ nodeId, ox: pt.x - node.x, oy: pt.y - node.y });
    setSelectedId(nodeId);
    setSelectedEdgeId(null);
  }, [connectingFrom, spaceDown, nodes, svgPoint]);

  const handleMouseMove = useCallback((e) => {
    const snap = (v) => Math.round(v / 20) * 20;
    if (dragging) {
      const pt = svgPoint(e);
      setNodes(prev => prev.map(n =>
        n.id === dragging.nodeId
          ? { ...n, x: snap(pt.x - dragging.ox), y: snap(pt.y - dragging.oy) }
          : n
      ));
    } else if (isPanning && panStart) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPan(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setPanStart({ x: e.clientX, y: e.clientY });
    } else if (connectingFrom) {
      const pt = svgPoint(e);
      setTempLine(prev => prev ? { ...prev, x2: pt.x, y2: pt.y } : null);
    }
  }, [dragging, isPanning, panStart, connectingFrom, svgPoint]);

  const handleMouseUp = useCallback(() => {
    if (dragging) {
      saveHistory(nodes, edges);
    }
    setDragging(null);
    setIsPanning(false);
    setPanStart(null);
  }, [dragging, nodes, edges, saveHistory]);

  // ── Canvas mouse down — pan on right-click, middle-click, or space+drag ──────
  const handleCanvasMouseDown = useCallback((e) => {
    const isPanTrigger = e.button === 1 || e.button === 2 || e.altKey || spaceDown;
    if (isPanTrigger) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      return;
    }
    if (!connectingFrom) {
      setSelectedId(null);
      setSelectedEdgeId(null);
    } else {
      setConnectingFrom(null);
      setTempLine(null);
    }
  }, [connectingFrom, spaceDown]);

  // ── Zoom: ctrl+scroll zooms, plain scroll pans ────────────────────────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      // Zoom toward cursor position
      const rect = svgRef.current.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      setZoom(prev => {
        const newZoom = Math.max(0.15, Math.min(4, prev * factor));
        // Adjust pan so the point under cursor stays fixed
        setPan(p => ({
          x: mx - (mx - p.x) * (newZoom / prev),
          y: my - (my - p.y) * (newZoom / prev),
        }));
        return newZoom;
      });
    } else {
      // Plain scroll pans the canvas
      setPan(prev => ({
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY,
      }));
    }
  }, []);

  // ── Drop from palette ────────────────────────────────────────────────────────
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('nodeType');
    if (!type || !NODE_TYPES[type]) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top  - pan.y) / zoom;
    const def = NODE_TYPES[type];
    const snap = (v) => Math.round(v / 20) * 20;
    const id = uid();
    const newNodes = [...nodes, {
      id,
      type,
      x: snap(x - def.w / 2),
      y: snap(y - def.h / 2),
      params: { ...def.defaultParams },
    }];
    setNodes(newNodes);
    saveHistory(newNodes, edges);
    setSelectedId(id);
    setSolved(false);
  }, [pan, zoom, nodes, edges, saveHistory]);

  // ── Port click (connect) ──────────────────────────────────────────────────────
  const handlePortClick = useCallback((nodeId, portKey, portKind) => {
    if (!connectingFrom) {
      // Start connection from an outlet port (or inlet — will auto-swap direction)
      if (portKind === 'out' || portKind === 'in') {
        const node = nodes.find(n => n.id === nodeId);
        const pos = portPosition(node, portKey);
        setConnectingFrom({ nodeId, portKey, portKind });
        setTempLine({ x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y });
      }
    } else {
      // Complete connection
      const fromKind = connectingFrom.portKind || 'out';
      // Determine direction: out→in or in→out (auto-swap if needed)
      let fromNodeId = connectingFrom.nodeId;
      let fromPort   = connectingFrom.portKey;
      let toNodeId   = nodeId;
      let toPort     = portKey;

      if (fromKind === 'in' && portKind === 'out') {
        // Swap: user drew from inlet to outlet — reverse it
        [fromNodeId, fromPort, toNodeId, toPort] = [nodeId, portKey, connectingFrom.nodeId, connectingFrom.portKey];
      } else if (fromKind === portKind) {
        // Same kind — invalid connection, cancel
        setConnectingFrom(null);
        setTempLine(null);
        return;
      }

      if (toNodeId !== fromNodeId) {
        const edgeId = `e_${uid()}`;
        const newEdges = [
          ...edges.filter(e => !(e.toNode === toNodeId && e.toPort === toPort)),
          { id: edgeId, fromNode: fromNodeId, fromPort, toNode: toNodeId, toPort },
        ];
        setEdges(newEdges);
        saveHistory(nodes, newEdges);
        setSolved(false);
      }
      setConnectingFrom(null);
      setTempLine(null);
    }
  }, [connectingFrom, nodes, edges, saveHistory]);

  // ── Solve flowsheet ───────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    const sheet = {
      version: 1,
      nodes: nodes.map(n => ({
        id: n.id, type: n.type, x: n.x, y: n.y,
        // Serialise params — Float64Arrays won't JSON correctly so convert
        params: JSON.parse(JSON.stringify(n.params)),
      })),
      edges,
      pan, zoom,
    };
    const blob = new Blob([JSON.stringify(sheet, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'flowsheet.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [nodes, edges, pan, zoom]);

  const handleLoad = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const sheet = JSON.parse(ev.target.result);
          if (!sheet.nodes || !sheet.edges) throw new Error('Invalid flowsheet file');
          setNodes(sheet.nodes);
          setEdges(sheet.edges);

          // Sync global _id counter to prevent collisions
          const allIds = [
            ...sheet.nodes.map(n => n.id),
            ...sheet.edges.map(e => e.id)
          ];
          allIds.forEach(id => {
            const numeric = parseInt(id, 36);
            if (!isNaN(numeric) && numeric >= _id) {
              _id = numeric + 1;
            }
          });

          if (sheet.pan) setPan(sheet.pan);
          if (sheet.zoom) setZoom(sheet.zoom);
          setResults({});
          setStreamStore({});
          setStreamByEdge({});
          setTearEdgeIds(new Set());
          setSolved(false);
          setSelectedId(null);
          setSelectedEdgeId(null);
        } catch (err) {
          alert('Failed to load flowsheet: ' + err.message);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, []);

  const handleSolve = useCallback(() => {
    // solveFlowsheet handles deduplication internally — just pass edges as-is
    const result = solveFlowsheet(nodes, edges, { maxIter, tol: solveTol, damp: solveDamp });
    setResults(result.resultStore);
    setStreamStore(result.streamStore);
    setStreamByEdge(result.streamByEdge || {});
    setStreamByEdgePersist(result.streamByEdge || {});
    setTearEdgeIds(result.tearEdgeIds || new Set());
    setSkippedEdgeIds(result.skippedEdgeIds || new Set());
    setSolveIter(result.iterations || 0);
    setSolveConverged(result.converged || false);
    setSolveMaxErr(result.maxErr ?? null);
    setSolved(true);
  }, [nodes, edges, maxIter, solveTol, solveDamp]);

  // Step: run exactly 1 outer iteration, starting from the last computed state
  const handleStep = useCallback(() => {
    const result = solveFlowsheet(nodes, edges, {
      maxIter: 1, tol: solveTol, damp: solveDamp,
      startFrom: streamByEdgePersist,
    });
    setResults(result.resultStore);
    setStreamStore(result.streamStore);
    setStreamByEdge(result.streamByEdge || {});
    setStreamByEdgePersist(result.streamByEdge || {});
    setTearEdgeIds(result.tearEdgeIds || new Set());
    setSkippedEdgeIds(result.skippedEdgeIds || new Set());
    setSolveIter(prev => prev + 1);
    setSolveConverged(result.converged || false);
    setSolveMaxErr(result.maxErr ?? null);
    setSolved(true);
  }, [nodes, edges, streamByEdgePersist, solveTol, solveDamp]);

  // Remove duplicate outlet edges — each port can only have one outgoing stream
  const handleFixWiring = useCallback(() => {
    const seen = new Set();
    const dupes = [];
    edges.forEach(e => {
      const outKey = `${e.fromNode}:${e.fromPort}`;
      const inKey  = `${e.toNode}:${e.toPort}`;
      if (seen.has(outKey) || seen.has(inKey)) {
        dupes.push(e.id);
      } else {
        seen.add(outKey);
        seen.add(inKey);
      }
    });
    if (dupes.length === 0) {
      alert('No duplicate connections found — wiring is clean.');
      return;
    }
    const newEdges = edges.filter(e => !dupes.includes(e.id));
    setEdges(newEdges);
    saveHistory(nodes, newEdges);
    setSolved(false);
    alert(`Removed ${dupes.length} duplicate connection${dupes.length > 1 ? 's' : ''}.`);
  }, [edges, nodes, saveHistory]);

  // ── Delete selected ───────────────────────────────────────────────────────────
  const handleDelete = useCallback(() => {
    if (selectedId) {
      const newNodes = nodes.filter(n => n.id !== selectedId);
      const newEdges = edges.filter(e => e.fromNode !== selectedId && e.toNode !== selectedId);
      setNodes(newNodes);
      setEdges(newEdges);
      saveHistory(newNodes, newEdges);
      setSelectedId(null);
      setSolved(false);
    }
    if (selectedEdgeId) {
      const newEdges = edges.filter(e => e.id !== selectedEdgeId);
      setEdges(newEdges);
      saveHistory(nodes, newEdges);
      setSelectedEdgeId(null);
      setSolved(false);
    }
  }, [selectedId, selectedEdgeId, nodes, edges, saveHistory]);

  // ── Update node param ─────────────────────────────────────────────────────────
  const handleParamChange = useCallback((key, val) => {
    const newNodes = nodes.map(n =>
      n.id === selectedId ? { ...n, params: { ...n.params, [key]: val } } : n
    );
    setNodes(newNodes);
    saveHistory(newNodes, edges);
    setSolved(false);
  }, [selectedId, nodes, edges, saveHistory]);

  useEffect(() => {
    const down = (e) => {
      if (e.code === 'Space' && !['INPUT','TEXTAREA'].includes(e.target.tagName)) {
        e.preventDefault();
        setSpaceDown(true);
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') &&
          !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
        handleDelete();
      }
      if (e.key === 'Escape') {
        setConnectingFrom(null);
        setTempLine(null);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        undo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    const up = (e) => { if (e.code === 'Space') setSpaceDown(false); };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [handleDelete, undo, redo]);

  // ─── Render ──────────────────────────────────────────────────────────────────
  const panelW = 280;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', minHeight: 500, gap: 0, background: 'var(--color-background-tertiary)' }}>

      {/* ── Left palette ───────────────────────────────────────────────────── */}
      <div style={{
        width: 180, flexShrink: 0, background: 'var(--color-background-secondary)',
        borderRight: '1px solid var(--color-border-tertiary)',
        overflowY: 'auto', padding: '8px 0',
      }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-tertiary)', padding: '4px 12px 8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Unit Operations
        </div>
        {PALETTE.map(cat => (
          <div key={cat.category}>
            <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--color-text-tertiary)', padding: '6px 12px 2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {cat.category}
            </div>
            {cat.types.map(type => {
              const def = NODE_TYPES[type];
              const colors = COLOR_MAP[def.color] || COLOR_MAP.gray;
              return (
                <div
                  key={type}
                  draggable
                  onDragStart={e => e.dataTransfer.setData('nodeType', type)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 12px', cursor: 'grab', fontSize: 12,
                    borderLeft: `3px solid ${colors.border}`,
                    margin: '2px 0',
                    background: 'transparent',
                    color: 'var(--color-text-primary)',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-background-primary)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ color: colors.border, fontSize: 14, minWidth: 16, textAlign: 'center' }}>{def.icon}</span>
                  {def.label}
                </div>
              );
            })}
          </div>
        ))}

        {/* Instructions */}
        <div style={{ padding: '16px 12px 8px', fontSize: 10, color: 'var(--color-text-tertiary)', lineHeight: 1.6 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Controls</div>
          <div>Drag unit onto canvas</div>
          <div>Click outlet port to start stream</div>
          <div>Click any inlet port to connect</div>
          <div><strong>Pan:</strong> right-click drag, scroll, or space+drag</div>
          <div><strong>Zoom:</strong> Ctrl+scroll or pinch</div>
          <div>Delete key removes selected</div>
          <div>Esc cancels connection</div>
          <div style={{ marginTop: 8, borderTop: '1px solid var(--color-border-tertiary)', paddingTop: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Stream legend</div>
            {[
              { color: '#2196f3', dash: null,    label: 'Vapour stream' },
              { color: '#4caf50', dash: '6,3',   label: 'Liquid stream' },
              { color: '#ff9800', dash: '8,4',   label: 'Tear (recycle — iterating)' },
              { color: '#888',    dash: null,    label: 'Empty / unsolved' },
              { color: '#ef4444', dash: '4,3',   label: 'Stale edge (ignored — delete it)' },
            ].map(({ color, dash, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <svg width={32} height={10} style={{ flexShrink: 0 }}>
                  <line x1={0} y1={5} x2={32} y2={5}
                    stroke={color} strokeWidth={2}
                    strokeDasharray={dash || undefined} />
                  <polygon points="29,2 32,5 29,8" fill={color} />
                </svg>
                <span style={{ fontSize: 10, color: 'var(--color-text-secondary)' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SVG Canvas ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {/* Toolbar — row 1: actions */}
        <div style={{
          position: 'absolute', top: 8, left: 8, right: 8, zIndex: 10,
          display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          {/* Row 1: main buttons */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={handleSolve} style={{
              padding: '6px 16px', fontSize: 13, fontWeight: 600,
              background: '#1d9e75', color: 'white', border: 'none',
              borderRadius: 6, cursor: 'pointer',
            }}>▶ Run</button>

            <button onClick={handleStep} title="Run exactly one outer iteration — watch the flowsheet approach steady state" style={{
              padding: '6px 12px', fontSize: 12, fontWeight: 500,
              background: '#1e40af', color: 'white', border: 'none',
              borderRadius: 6, cursor: 'pointer',
            }}>⏭ Step</button>

            <button onClick={handleSave} style={{
              padding: '6px 10px', fontSize: 12,
              background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-secondary)',
              borderRadius: 6, cursor: 'pointer', color: 'var(--color-text-primary)',
            }}>↓ Save</button>
            <button onClick={handleLoad} style={{
              padding: '6px 10px', fontSize: 12,
              background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-secondary)',
              borderRadius: 6, cursor: 'pointer', color: 'var(--color-text-primary)',
            }}>↑ Load</button>

            <div style={{ width: 1, height: 20, background: 'var(--color-border-tertiary)' }} />

            <button onClick={undo} disabled={historyIdx <= 0} style={{
              padding: '6px 8px', fontSize: 12,
              background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-secondary)',
              borderRadius: 6, cursor: historyIdx <= 0 ? 'default' : 'pointer',
              color: historyIdx <= 0 ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
              opacity: historyIdx <= 0 ? 0.5 : 1,
            }}>↶ Undo</button>
            <button onClick={redo} disabled={historyIdx >= history.length - 1} style={{
              padding: '6px 8px', fontSize: 12,
              background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-secondary)',
              borderRadius: 6, cursor: historyIdx >= history.length - 1 ? 'default' : 'pointer',
              color: historyIdx >= history.length - 1 ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
              opacity: historyIdx >= history.length - 1 ? 0.5 : 1,
            }}>↷ Redo</button>

            <div style={{ width: 1, height: 20, background: 'var(--color-border-tertiary)' }} />

            <button onClick={handleFixWiring} title="Remove duplicate outlet connections" style={{
              padding: '6px 8px', fontSize: 12,
              background: 'var(--color-background-secondary)', border: '1px solid #d97706',
              borderRadius: 6, cursor: 'pointer', color: '#d97706', fontWeight: 500,
            }}>⚡ Fix Wiring</button>

            <div style={{ flex: 1 }} />

            {/* Convergence status indicator */}
            {solved && (
              <span style={{
                fontSize: 11, fontWeight: 500, padding: '3px 8px', borderRadius: 4,
                background: solveConverged ? '#d1fae5' : '#fef3c7',
                color: solveConverged ? '#065f46' : '#92400e',
              }}>
                {solveConverged ? `✓ Converged` : `⚠ Not converged`}
                {' — '}{solveIter} iter
                {solveMaxErr != null && !solveConverged && (
                  <span style={{ marginLeft: 4, opacity: 0.8 }}>
                    (err: {solveMaxErr < 0.001 ? solveMaxErr.toExponential(1) : solveMaxErr.toFixed(4)})
                  </span>
                )}
              </span>
            )}
            {!solved && nodes.length > 0 && (
              <span style={{ fontSize: 11, color: '#f59e0b' }}>● Unsolved — click Run</span>
            )}
            {connectingFrom && (
              <span style={{ fontSize: 11, color: '#2196f3' }}>Drawing stream — click inlet port to connect, Esc to cancel</span>
            )}

            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Zoom: {(zoom * 100).toFixed(0)}%</span>
            <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={{
              padding: '4px 8px', fontSize: 11,
              background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)',
              borderRadius: 4, cursor: 'pointer', color: 'var(--color-text-primary)',
            }}>Reset view</button>
          </div>

          {/* Row 2: convergence controls */}
          <div style={{
            display: 'flex', gap: 10, alignItems: 'center',
            background: 'var(--color-background-secondary)',
            border: '1px solid var(--color-border-tertiary)',
            borderRadius: 6, padding: '4px 10px', fontSize: 11,
            color: 'var(--color-text-secondary)',
          }}>
            <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>Convergence:</span>

            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Max iter
              <input type="number" min={1} max={500} value={maxIter}
                onChange={e => setMaxIter(Math.max(1, parseInt(e.target.value) || 80))}
                style={{ width: 50, padding: '1px 4px', fontSize: 11,
                  background: 'var(--color-background-primary)',
                  border: '1px solid var(--color-border-tertiary)',
                  borderRadius: 3, color: 'var(--color-text-primary)' }} />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Tolerance
              <input type="number" min={0.0001} max={0.1} step={0.0001} value={solveTol}
                onChange={e => setSolveTol(Math.max(1e-6, parseFloat(e.target.value) || 0.001))}
                style={{ width: 70, padding: '1px 4px', fontSize: 11,
                  background: 'var(--color-background-primary)',
                  border: '1px solid var(--color-border-tertiary)',
                  borderRadius: 3, color: 'var(--color-text-primary)' }} />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Damping
              <input type="range" min={0.1} max={1.0} step={0.05} value={solveDamp}
                onChange={e => setSolveDamp(parseFloat(e.target.value))}
                style={{ width: 80 }} />
              <span style={{ minWidth: 28 }}>{(solveDamp * 100).toFixed(0)}%</span>
            </label>

            <span style={{ color: 'var(--color-text-tertiary)', fontSize: 10 }}>
              Step = 1 iter from current state · Run = full solve from seed
            </span>
          </div>
        </div>

        {/* Canvas SVG — offset below the two-row toolbar (~80px) */}
        <svg
          ref={svgRef}
          style={{ width: '100%', height: 'calc(100% - 72px)', marginTop: 72, cursor: spaceDown ? (isPanning ? 'grabbing' : 'grab') : isPanning ? 'grabbing' : connectingFrom ? 'crosshair' : 'default' }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseDown={handleCanvasMouseDown}
          onWheel={handleWheel}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onContextMenu={e => e.preventDefault()}
        >
          <defs>
            <marker id="streamArrow" viewBox="0 0 10 10" refX="8" refY="5"
              markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke"
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </marker>
            <marker id="pumpArrow" viewBox="0 0 10 10" refX="8" refY="5"
              markerWidth="5" markerHeight="5" orient="auto">
              <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke"
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </marker>
            {/* Grid pattern */}
            <pattern id="grid" width={20 * zoom} height={20 * zoom} patternUnits="userSpaceOnUse"
              x={pan.x % (20 * zoom)} y={pan.y % (20 * zoom)}>
              <path d={`M ${20 * zoom} 0 L 0 0 0 ${20 * zoom}`}
                fill="none" stroke="var(--color-border-tertiary)" strokeWidth="0.5" opacity="0.4" />
            </pattern>
          </defs>

          {/* Grid background */}
          <rect width="100%" height="100%" fill="url(#grid)" />

          <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>

            {/* Edges */}
            {edges.map(edge => (
              <CanvasEdge
                key={edge.id}
                edge={edge}
                nodes={nodes}
                streamByEdge={streamByEdge}
                selected={edge.id === selectedEdgeId}
                isTear={tearEdgeIds.has(edge.id)}
                isSkipped={skippedEdgeIds.has(edge.id)}
                onClick={id => { setSelectedEdgeId(id); setSelectedId(null); }}
              />
            ))}

            {/* Temp connection line */}
            {tempLine && (
              <line
                x1={tempLine.x1} y1={tempLine.y1}
                x2={tempLine.x2} y2={tempLine.y2}
                stroke="#2196f3" strokeWidth={1.5} strokeDasharray="6,3"
              />
            )}

            {/* Nodes */}
            {nodes.map(node => (
              <CanvasNode
                key={node.id}
                node={node}
                selected={node.id === selectedId}
                results={results[node.id]}
                onSelect={id => { setSelectedId(id); setSelectedEdgeId(null); }}
                onDragStart={handleDragStart}
                connectingFrom={connectingFrom}
                onPortClick={handlePortClick}
                streamStore={streamStore}
              />
            ))}

            {/* Empty canvas hint */}
            {nodes.length === 0 && (
              <text x="50%" y="45%" textAnchor="middle" fontSize={14}
                fill="var(--color-text-tertiary)" style={{ userSelect: 'none' }}>
                Drag unit operations from the left panel onto the canvas
              </text>
            )}
          </g>
        </svg>
      </div>

      {/* ── Right panel: properties + results ──────────────────────────────── */}
      <div style={{
        width: panelW, flexShrink: 0,
        background: 'var(--color-background-secondary)',
        borderLeft: '1px solid var(--color-border-tertiary)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border-tertiary)' }}>
          {['properties', 'streams'].map(tab => (
            <button key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1, padding: '8px 4px', fontSize: 12, fontWeight: 500,
                border: 'none', cursor: 'pointer', textTransform: 'capitalize',
                background: activeTab === tab ? 'var(--color-background-primary)' : 'transparent',
                color: activeTab === tab ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                borderBottom: activeTab === tab ? '2px solid #1d9e75' : '2px solid transparent',
              }}>
              {tab}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>

          {/* ── Properties tab ── */}
          {activeTab === 'properties' && (
            <>
              {selectedNode ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, color: 'var(--color-text-primary)' }}>
                    {NODE_TYPES[selectedNode.type]?.label}
                    <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-tertiary)', marginLeft: 8 }}>
                      {selectedNode.id}
                    </span>
                  </div>

                  {/* Calculated results */}
                  {results[selectedNode.id] && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-tertiary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Results</div>
                      {Object.entries(results[selectedNode.id]).map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0', borderBottom: '1px solid var(--color-border-tertiary)' }}>
                          <span style={{ color: 'var(--color-text-secondary)' }}>{k}</span>
                          <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Parameters */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-tertiary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Parameters</div>
                    <ParamEditor node={selectedNode} onChange={handleParamChange} results={results[selectedNode.id]} />
                  </div>

                  <button
                    onClick={handleDelete}
                    style={{
                      marginTop: 16, width: '100%', padding: '6px', fontSize: 12,
                      background: 'transparent', border: '1px solid var(--color-border-secondary)',
                      borderRadius: 4, color: 'var(--color-text-danger)', cursor: 'pointer',
                    }}>
                    Delete node
                  </button>
                </>
              ) : selectedEdge ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Stream</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                    {selectedEdge.fromNode}.{selectedEdge.fromPort} → {selectedEdge.toNode}.{selectedEdge.toPort}
                  </div>
                  <StreamTable stream={streamByEdge?.[selectedEdge.id]} />
                  <button
                    onClick={handleDelete}
                    style={{
                      marginTop: 12, width: '100%', padding: '6px', fontSize: 12,
                      background: 'transparent', border: '1px solid var(--color-border-secondary)',
                      borderRadius: 4, color: 'var(--color-text-danger)', cursor: 'pointer',
                    }}>
                    Delete stream
                  </button>
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.7 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8, color: 'var(--color-text-primary)' }}>Process Canvas</div>
                  <p>Build a custom gas plant flowsheet by dragging unit operations onto the canvas.</p>
                  <p>Connect by clicking outlet ports then inlet ports. Click <strong>Run</strong> to solve.</p>
                  <div style={{ marginTop: 12, fontSize: 11, fontWeight: 500, color: 'var(--color-text-secondary)' }}>Refrigerated Lean Oil Plant</div>
                  <ol style={{ paddingLeft: 16, marginTop: 4, lineHeight: 2, fontSize: 11 }}>
                    <li>Feed → Separator (inlet scrubber)</li>
                    <li>Sep (vap) → Absorber (gas in) at −20°C</li>
                    <li>Lean oil Feed → Chiller (−20°C) → Absorber (solvent in)</li>
                    <li>Absorber (rich oil) → HX (rich/lean exchanger)</li>
                    <li>HX (rich oil warm) → JT Valve → Flash Separator</li>
                    <li>Flash Sep (vap) → Recip Compressor → plant inlet</li>
                    <li>Flash Sep (liq) → HX (trim heat to ~40°C) → De-ethanizer</li>
                    <li>De-ethanizer OH → Partial Condenser → vap recycle + C2 product</li>
                    <li>De-ethanizer btms → Feed/Btms HX → Still (150°C, lkIdx=2 hkIdx=3)</li>
                    <li>Still OH → Partial Condenser → NGL liquid product</li>
                    <li>Still btms (lean oil) → Feed/Btms HX → Chiller → Absorber</li>
                  </ol>
                  <div style={{ marginTop: 8, fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                    Tip: Set lkIdx/hkIdx in column params — 0=CH4, 1=C2, 2=C3, 3=C4, 4=C7, 5=C8, 6=C9, 7=N2
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Streams tab: mass balance table ── */}
          {activeTab === 'streams' && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Stream Summary</div>
              {!solved ? (
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Click Run to solve</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                        <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 500 }}>From → To</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 500 }}>F (kmol/h)</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 500 }}>T (°C)</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 500 }}>P (bar)</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 500 }}>Ph</th>
                      </tr>
                    </thead>
                    <tbody>
                      {edges.map(e => {
                        const s = streamByEdge?.[e.id];
                        const fn = nodes.find(n => n.id === e.fromNode);
                        const tn = nodes.find(n => n.id === e.toNode);
                        const isStale = skippedEdgeIds.has(e.id);
                        if (!s?.valid && !isStale) return null;
                        return (
                          <tr key={e.id}
                            style={{
                              borderBottom: '1px solid var(--color-border-tertiary)',
                              cursor: 'pointer',
                              background: isStale
                                ? 'rgba(239,68,68,0.08)'
                                : selectedEdgeId === e.id
                                  ? 'var(--color-background-info)'
                                  : 'transparent',
                            }}
                            onClick={() => { setSelectedEdgeId(e.id); setSelectedId(null); setActiveTab('properties'); }}>
                            <td style={{ padding: '3px 6px', fontSize: 10,
                              color: isStale ? '#ef4444' : 'var(--color-text-secondary)' }}>
                              {isStale && '✕ STALE: '}
                              {fn?.type}/{e.fromPort} → {tn?.type}/{e.toPort}
                              {isStale && ' (ignored by solver — delete this edge)'}
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', color: isStale ? '#ef4444' : undefined }}>
                              {s?.valid ? s.F_kmols.toFixed(1) : '—'}
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                              {s?.valid ? (s.T_K - 273.15).toFixed(1) : '—'}
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                              {s?.valid ? s.P_bar.toFixed(2) : '—'}
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'center',
                              color: s?.phase === 'V' ? '#2196f3' : s?.phase === 'L' ? '#4caf50' : '#ff9800' }}>
                              {s?.valid ? s.phase : '—'}
                            </td>
                          </tr>
                        );
                      }).filter(Boolean)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
